import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {DestinationPickerPopover} from 'components/DestinationPickerPopover'
import {usePreheatRooms} from 'components/Offer/hooks/usePreheatRooms'
import {
  OnDestinationChangeParams,
  OpenedFormControl,
  RecentSearchParams,
  useSearchBox,
  useSearchBoxDestinationPicker
} from 'hooks/searchBox'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getOptimizedRoomsConfig} from 'modules/sapiSearch/selectors'
import {getDateValidationReason} from 'modules/search/selectors'
import {getHotelResult} from 'modules/searchApi/selectors'
import {getOpenDatePickerType} from 'modules/searchBox/selectors'
import {
  setOpenDatePickerType,
  setSearchBoxDestinationDisplayValue
} from 'modules/searchBox/slice'
import {SearchTrigger} from 'modules/searchBox/types'
import {getDestinationParameter} from 'utils/searchParams'

import {OutsideClickHandler} from '@vio/atlas/helpers/OutsideClickHandler'
import {PopoverPlacement} from '@vio/atlas/Popover'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  TrackEventPayload
} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {ConfigureI18nContext} from '@vio/core/src/localization/components/ConfigureI18n'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {
  roomsSplitToString,
  roomsToConfigurationObject
} from '@vio/core/src/room/business/roomConfiguration'
import {
  RoomConfigType,
  RoomSplitType
} from '@vio/core/src/room/types/RoomConfiguration'
import {POPOVER_VERTICAL_OFFSET} from '@vio/shared/src/search/constants'
import {DatePickerPopover} from '@vio/shared/src/search/DatePickerPopover'
import {GuestPickerPopover} from '@vio/shared/src/search/GuestPickerPopover'
import {GuestRoomConfigPopover} from '@vio/shared/src/search/GuestRoomConfig/GuestRoomConfigPopover'
import {SearchFormHorizontal} from '@vio/shared/src/search/SearchFormHorizontal'
import {SearchFormHorizontalFullWidth} from '@vio/shared/src/whiteLabel/SearchFormHorizontalFullWidth'

import {UpdateSearch} from '.'

const FormContainer = styled.div`
  width: 100%;
  display: flex;
`

interface Props {
  checkIn: string
  checkOut: string
  rooms: string
  optimizeRooms?: boolean
  showOptimizedRoomsOption: boolean
  updateSearch: UpdateSearch
}

export const SearchForm = ({
  checkIn,
  checkOut,
  rooms,
  optimizeRooms,
  showOptimizedRoomsOption,
  updateSearch
}: Props) => {
  const hotelId = useSelector(getAccommodationPageHotelId)
  const determinedRoomsConfig = useSelector(state =>
    getOptimizedRoomsConfig(state, hotelId)
  )
  const {data: hotel} = useSelector(state => getHotelResult(state, hotelId))
  const {triggerRoomsPreheat} = usePreheatRooms()
  const {doSearch} = useSearchBox()
  const i18n = useContext(ConfigureI18nContext)
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const containerRef = useRef<HTMLDivElement>(null)
  const guestPickerRef = useRef<HTMLDivElement>(null)
  const stateOpenedDatePickerType = useSelector(getOpenDatePickerType)
  const [openedFormControl, setOpenedFormControl] = useState<OpenedFormControl>(
    OpenedFormControl.NONE
  )
  const {shell} = useBrandCapabilities()
  const hasAlternativeSearchForm =
    shell.hasAlternativeSearchForm.enabled &&
    !shell.hasAlternativeSearchForm.config.isHomepageOnly
  const dateValidationReason = useSelector(getDateValidationReason)

  const destinationInputRef = useRef<HTMLDivElement>(null)

  // Input state
  const [newCheckIn, setNewCheckIn] = useState<string>(checkIn)
  const [newCheckOut, setNewCheckOut] = useState<string>(checkOut)
  const [newRooms, setNewRooms] = useState<string>(rooms)
  const [newOptimizeRooms, setNewOptimizeRooms] = useState<boolean | undefined>(
    optimizeRooms
  )
  const {numberOfAdults, numberOfChildren, numberOfRooms, roomsSplit} =
    roomsToConfigurationObject(newRooms)

  useEffect(() => {
    if (stateOpenedDatePickerType === DatePickerType.CheckIn) {
      setOpenedFormControl(OpenedFormControl.CHECK_IN)
    }
  }, [stateOpenedDatePickerType])

  useEffect(() => {
    if (hotel) {
      dispatch(setSearchBoxDestinationDisplayValue(hotel.hotelName))
    }
  }, [dispatch, hotel])

  // Keep internal/external state in sync
  useEffect(() => {
    setNewCheckIn(checkIn)
    setNewCheckOut(checkOut)
    setNewRooms(rooms)
    setNewOptimizeRooms(optimizeRooms)
  }, [checkIn, checkOut, rooms, optimizeRooms])

  const trackDatePickerClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.DatePickerInput,
        action: Action.Clicked
      })
    )
  }
  const handleReset = () => {
    setNewCheckIn(checkIn)
    setNewCheckOut(checkOut)
    setNewRooms(rooms)
    setNewOptimizeRooms(optimizeRooms)
  }

  const handleSubmit = () => {
    updateSearch({
      checkIn: newCheckIn,
      checkOut: newCheckOut,
      rooms: newRooms,
      optimizeRooms: newOptimizeRooms,
      searchTrigger: SearchTrigger.Submit
    })
  }

  const handleSearchButtonClick = () => {
    handleSubmit()
  }

  const handleCheckInClick = () => {
    trackDatePickerClick()
    setOpenedFormControl(OpenedFormControl.CHECK_IN)
  }

  const handleCheckOutClick = () => {
    trackDatePickerClick()
    setOpenedFormControl(OpenedFormControl.CHECK_OUT)
  }

  const handleDateChange = (checkInDate: string, checkOutDate: string) => {
    if (checkInDate && checkOutDate) triggerRoomsPreheat()

    setNewCheckIn(checkInDate)
    setNewCheckOut(checkOutDate)
  }

  const handleDatePickerOpen = (datePickerType: DatePickerType) => {
    const openedControl = {
      [DatePickerType.CheckIn]: OpenedFormControl.CHECK_IN,
      [DatePickerType.CheckOut]: OpenedFormControl.CHECK_OUT
    }[datePickerType]
    setOpenedFormControl(openedControl)
  }

  const handleCloseDatePicker = () => {
    handleCloseSearchBox()
    dispatch(setOpenDatePickerType(null))
  }
  const handleDatePickerSubmit = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.DatePickerSubmitButton,
        action: Action.Clicked
      })
    )
    handleCloseDatePicker()

    updateSearch({
      checkIn: newCheckIn,
      checkOut: newCheckOut,
      rooms: newRooms,
      optimizeRooms: newOptimizeRooms,
      searchTrigger: SearchTrigger.DatePicker
    })
  }

  const handleGuestsClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.GuestPickerInput,
        action: Action.Clicked
      })
    )
    setOpenedFormControl(OpenedFormControl.GUEST)
  }

  const handleGuestPickerClose = () => {
    handleCloseSearchBox()
  }

  const handleGuestChange = (
    newRoomsSplit: RoomSplitType[],
    shouldPerformSearch?: boolean
  ) => {
    const newRooms = roomsSplitToString(newRoomsSplit)
    setNewRooms(newRooms)

    if (shouldPerformSearch) {
      updateSearch({
        rooms: newRooms,
        checkIn: newCheckIn,
        checkOut: newCheckOut,
        optimizeRooms: newOptimizeRooms,
        searchTrigger: SearchTrigger.GuestPicker
      })
    }
  }
  const handleGuestOptimizeRoomsChange = (isOptimizeRooms: boolean): void => {
    if (newOptimizeRooms && !isOptimizeRooms && determinedRoomsConfig) {
      setNewRooms(determinedRoomsConfig)
    }
    setNewOptimizeRooms(isOptimizeRooms)
  }

  const handleGuestSubmit = (newRoomsSplit: RoomSplitType[]) => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.GuestPickerSubmitButton,
        action: Action.Clicked
      })
    )
    handleGuestPickerClose()
    handleGuestChange(newRoomsSplit, true)
  }

  const handleOutsideClick = () => {
    if (
      checkIn !== newCheckIn ||
      checkOut !== newCheckOut ||
      rooms !== newRooms ||
      optimizeRooms !== newOptimizeRooms
    ) {
      handleReset()
    }
  }

  const isCheckInPickerOpen = openedFormControl === OpenedFormControl.CHECK_IN
  const isCheckOutPickerOpen = openedFormControl === OpenedFormControl.CHECK_OUT
  const isGuestPickerOpen = openedFormControl === OpenedFormControl.GUEST
  const isDestinationPickerOpen =
    openedFormControl === OpenedFormControl.DESTINATION

  const openedDatePickerType: DatePickerType | null = {
    [OpenedFormControl.CHECK_IN]: DatePickerType.CheckIn,
    [OpenedFormControl.CHECK_OUT]: DatePickerType.CheckOut,
    [OpenedFormControl.GUEST]: null,
    [OpenedFormControl.NONE]: null
  }[openedFormControl]

  const {
    destinationHasError,
    onDestinationPickerInputClick,
    destinationDisplayValue,
    destinationPickerInputDisplayValue,
    onSuggestionsRequested,
    onRecentSearchSelected,
    onNearbyPropertiesSuggestionSelected,
    onDestinationChange
  } = useSearchBoxDestinationPicker()

  const handleDestinationClick = () => {
    setOpenedFormControl(OpenedFormControl.DESTINATION)
    onDestinationPickerInputClick()
  }

  const handleCloseSearchBox = () => {
    setOpenedFormControl(OpenedFormControl.NONE)
  }

  const handleRecentSearchSelected = useCallback(
    (recentSearch: RecentSearchParams): void => {
      onRecentSearchSelected({recentSearch})
      dispatch(setOpenDatePickerType(DatePickerType.CheckIn))
    },
    [dispatch, onRecentSearchSelected]
  )

  const handleDestinationChange = (params: OnDestinationChangeParams) => {
    onDestinationChange(params)

    const {type, value} = params

    doSearch({
      newUrlParams: {
        // The date should reflect the date picker value, not the recent search date.
        checkIn,
        checkOut,
        rooms,
        ...getDestinationParameter(type, value)
      },
      searchTrigger: SearchTrigger.Suggestions
    })
  }

  const handleSuggestionSelected = useCallback((): void => {
    dispatch(setOpenDatePickerType(DatePickerType.CheckIn))
  }, [dispatch])

  const handleCloseDestinationPicker = (e: MouseEvent) => {
    // do not close the destination picker if we are clicking within the input container
    if (!destinationInputRef?.current?.contains(e.target as Node)) {
      handleCloseSearchBox()
    }
  }

  const handleNearbyPropertiesSuggestionSelected = useCallback((): void => {
    onNearbyPropertiesSuggestionSelected()
    dispatch(setOpenDatePickerType(DatePickerType.CheckIn))
  }, [dispatch, onNearbyPropertiesSuggestionSelected])

  const trackEventCallback = useCallback(
    (payload: TrackEventPayload) => dispatchTeamEvent(trackEvent(payload)),
    [dispatchTeamEvent]
  )

  const getSearchFormComponent = () => {
    if (hasAlternativeSearchForm) {
      return SearchFormHorizontalFullWidth
    }

    return SearchFormHorizontal
  }

  const SearchFormComponent = getSearchFormComponent()

  return (
    <>
      <FormContainer ref={containerRef}>
        <OutsideClickHandler
          display="contents"
          onOutsideClick={handleOutsideClick}
        >
          <SearchFormComponent
            checkIn={newCheckIn}
            checkOut={newCheckOut}
            numberOfAdults={numberOfAdults}
            numberOfChildren={numberOfChildren}
            numberOfRooms={numberOfRooms}
            isRoomsLabelShown={!(showOptimizedRoomsOption && newOptimizeRooms)}
            onCheckInDatePickerClick={handleCheckInClick}
            onCheckOutDatePickerClick={handleCheckOutClick}
            onGuestPickerClick={handleGuestsClick}
            onSubmit={handleSearchButtonClick}
            isCheckInPickerOpen={isCheckInPickerOpen}
            isCheckOutPickerOpen={isCheckOutPickerOpen}
            isGuestPickerOpen={isGuestPickerOpen}
            guestPickerRef={guestPickerRef}
            destinationHasError={destinationHasError}
            isDestinationPickerOpen={isDestinationPickerOpen}
            onDestinationPickerClick={handleDestinationClick}
            destinationButtonDisplayValue={destinationDisplayValue}
            destinationInputDisplayValue={destinationPickerInputDisplayValue}
            onSuggestionsRequested={onSuggestionsRequested}
            destinationInputRef={destinationInputRef}
            isDateConfirmationTooltipShown={!!dateValidationReason}
            dateValidationReason={dateValidationReason}
            trackEvent={trackEventCallback}
          />
          <DestinationPickerPopover
            isOpen={isDestinationPickerOpen}
            anchorRef={containerRef}
            onChange={handleDestinationChange}
            onSuggestionSelected={handleSuggestionSelected}
            onRecentSearchSelected={handleRecentSearchSelected}
            onClose={handleCloseDestinationPicker}
            onNearbyPropertiesSuggestionSelected={
              handleNearbyPropertiesSuggestionSelected
            }
            verticalOffset={POPOVER_VERTICAL_OFFSET}
          />

          <DatePickerPopover
            isOpen={isCheckInPickerOpen || isCheckOutPickerOpen}
            openedDatePickerType={openedDatePickerType}
            anchorRef={containerRef}
            checkIn={newCheckIn}
            checkOut={newCheckOut}
            onClose={handleCloseDatePicker}
            onChange={handleDateChange}
            onDatePickerOpen={handleDatePickerOpen}
            onSubmit={handleDatePickerSubmit}
            verticalOffset={POPOVER_VERTICAL_OFFSET}
            weekdaysShort={i18n.weekDayNamesShort}
            months={i18n.monthNames}
            firstDayOfWeek={i18n.firstDayOfWeek}
          />
          {showOptimizedRoomsOption ? (
            <GuestRoomConfigPopover
              isOpen={isGuestPickerOpen}
              anchorRef={guestPickerRef}
              placement={PopoverPlacement.Left}
              roomsSplit={roomsSplit}
              onRoomsChange={handleGuestChange}
              onOptimizeRoomsChange={handleGuestOptimizeRoomsChange}
              onSubmit={handleGuestSubmit}
              onClose={handleGuestPickerClose}
              roomConfigType={
                newOptimizeRooms
                  ? RoomConfigType.Optimized
                  : RoomConfigType.Manual
              }
              submitButtonMessage={
                <FormattedMessageWrapper id="search" defaultMessage="Search" />
              }
              verticalOffset={
                hasAlternativeSearchForm
                  ? POPOVER_VERTICAL_OFFSET
                  : POPOVER_VERTICAL_OFFSET + 4
              }
            />
          ) : (
            <GuestPickerPopover
              isOpen={isGuestPickerOpen}
              anchorRef={guestPickerRef}
              placement={PopoverPlacement.Left}
              roomsSplit={roomsSplit}
              onChange={handleGuestChange}
              onSubmit={handleGuestSubmit}
              onClose={handleGuestPickerClose}
              verticalOffset={
                hasAlternativeSearchForm
                  ? POPOVER_VERTICAL_OFFSET
                  : POPOVER_VERTICAL_OFFSET + 4
              }
            />
          )}
        </OutsideClickHandler>
      </FormContainer>
    </>
  )
}
